import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Snackbar,
  Switch,
} from '@mui/material';
import { v4 } from 'uuid';

import { gql } from '@graphql/__generated__';
import { type ListingBuyersAutoMatchingEmailFragment } from '@graphql/__generated__/graphql';
import { ConfirmDialog } from '@modules/_shared/components/ConfirmDialog';
import { EmailTemplateForm } from '@modules/_shared/components/EmailTemplateForm';
import {
  CopyRecipientsTrackingHint,
  hasCopyRecipients,
} from '@modules/_shared/components/listing-marketing/CopyRecipientsTrackingHint';
import {
  EmailSenderStatusAlert,
  hasEmailSendingAccount,
} from '@modules/_shared/components/listing-marketing/EmailSenderStatusAlert';
import { createEmailTemplateMutationData } from '@modules/_shared/components/listing-marketing/emailTemplateMutation';
import { UPSERT_EMAIL_TEMPLATE } from '@modules/_shared/components/listing-marketing/queries';
import { useEmailSender } from '@modules/_shared/components/listing-marketing/useEmailSender';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useMatchedBuyerLeadEmailTemplate } from '@modules/_shared/utils/matchedBuyerLeadEmailTemplate';
import { useLocale } from '@shared/hooks/locale';

type BuyersAutoMatchingEmailFormProps = {
  listing: ListingBuyersAutoMatchingEmailFragment;
  disabled?: boolean;
};

export type EmailTemplateFormFragment = {
  id: string;
  name?: string | null;
  cc?: any | null;
  bcc?: any | null;
  subject?: string | null;
  text?: string | null;
  add_signature: boolean;
  is_shared_with_organisation: boolean;
  is_shared_with_everyone: boolean;
  email_templates_files: Array<{
    email_template_id: string;
    file_id: string;
    file: {
      id: string;
      name: string;
      url: string;
      size: number;
      mimetype?: string | null;
    };
  }>;
};

export const LISTING_BUYERS_AUTO_MATCHING_EMAIL_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingBuyersAutoMatchingEmail on lots {
    id
    tenant_id
    auto_matching_response_enabled
    listing_url
    auto_matching_email_templates: email_templates(
      where: { type: { _eq: "buyers_auto_matching" } }
      order_by: { updated_at: desc_nulls_last }
      limit: 1
    ) {
      ...EmailTemplateForm
    }
    automatching_sender {
      ...EmailSender
    }
    broker {
      ...EmailSender
    }
    ...EmailTemplateFormListing
  }
`);

const UPDATE_LISTING = gql(/* GraphQL */ `
  mutation updateBuyerAutoMatchingEmail($id: uuid!, $lot: lots_set_input) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $lot) {
      id
      ...ListingBuyersAutoMatchingEmail
    }
  }
`);

export const BuyersAutoMatchingEmailForm: React.FC<
  BuyersAutoMatchingEmailFormProps
> = ({ listing, disabled = false }) => {
  const { t, language } = useLocale();
  const [updateListing, { loading: updatingListing }] =
    useMutation(UPDATE_LISTING);
  const [upsertEmailTemplate] = useMutation(UPSERT_EMAIL_TEMPLATE);

  const [confirmDialog, setConfirmDialog] = useState(false);
  const [showSuccessUpdateMessage, setShowSuccessUpdateMessage] =
    useState(false);

  const initialSender = listing.automatching_sender ?? listing.broker;
  const { sender: responseSender, selectSender } = useEmailSender(
    initialSender ?? null,
  );

  const autoMatchingTemplate =
    listing.auto_matching_email_templates?.[0] ?? null;
  const [showCopyRecipientsHint, setShowCopyRecipientsHint] = useState(() =>
    hasCopyRecipients(autoMatchingTemplate?.cc, autoMatchingTemplate?.bcc),
  );

  const {
    emailTemplate: defaultEmailTemplate,
    loading: isLoadingDefaultEmailTemplate,
    error: defaultEmailTemplateError,
  } = useMatchedBuyerLeadEmailTemplate({
    listing,
    language,
    t,
    skip: autoMatchingTemplate != null,
  });

  const canSendEmails = hasEmailSendingAccount(responseSender);
  const canEnableSending =
    autoMatchingTemplate != null && responseSender != null;

  return (
    <>
      <MutationErrorSnackbar error={defaultEmailTemplateError ?? null} />
      <Box
        sx={{
          pt: 1,
          position: 'relative',
          pointerEvents: disabled ? 'none' : 'initial',
        }}
      >
        {disabled && (
          <Box
            sx={{
              position: 'absolute',
              borderRadius: 1,
              top: -5,
              right: -5,
              left: -5,
              bottom: -5,
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'center',
            }}
          >
            <Alert
              severity="info"
              sx={{
                mt: '30%',
              }}
            >
              {t('Enable auto-matching in order to activate automated emails')}
            </Alert>
          </Box>
        )}
        <FormControlLabel
          disabled={!canEnableSending || updatingListing || disabled}
          sx={{
            pointerEvents: updatingListing ? 'none' : 'initial',
          }}
          control={
            <Switch
              onChange={() => {
                updateListing({
                  variables: {
                    id: listing.id,
                    lot: {
                      auto_matching_response_enabled:
                        !listing.auto_matching_response_enabled,
                    },
                  },
                });
              }}
              checked={listing.auto_matching_response_enabled ?? false}
            />
          }
          label={t('Send email automatically')}
        />
        <FormHelperText>
          {!canEnableSending &&
            t('Please fill the template below to enable auto-matching')}
        </FormHelperText>
        <Box
          sx={{
            mt: 2,
          }}
        >
          <EmailSenderStatusAlert sender={responseSender} />
          <CopyRecipientsTrackingHint show={showCopyRecipientsHint} />

          {isLoadingDefaultEmailTemplate ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <EmailTemplateForm
              emailTemplate={
                autoMatchingTemplate ?? defaultEmailTemplate ?? undefined
              }
              isTemplateShareable={false}
              disabled={disabled}
              allowSubmitNotDirty={autoMatchingTemplate == null}
              fields={['cc', 'bcc', 'from']}
              initialSender={initialSender}
              lotSnippetData={listing}
              onChange={async (data, name, type) => {
                setShowCopyRecipientsHint(
                  hasCopyRecipients(data?.cc, data?.bcc),
                );
                if (type === 'change' && name === 'from') {
                  await selectSender(data?.from);
                }
              }}
              onSubmit={async input => {
                const emailTemplateId = input.id ?? v4();
                const emailTemplateMutationData =
                  createEmailTemplateMutationData({
                    emailTemplateId,
                    input,
                    owner: { listingId: listing.id },
                    type: 'buyers_auto_matching',
                  });

                await upsertEmailTemplate({
                  variables: {
                    listingId: listing.id,
                    emailTemplateId,
                    ...emailTemplateMutationData,
                    listingUpdates: {
                      automatching_sender_id: input.from ?? null,
                    },
                  },
                  onCompleted: () => setShowSuccessUpdateMessage(true),
                });

                if (
                  canSendEmails &&
                  listing.auto_matching_response_enabled === false
                ) {
                  setConfirmDialog(true);
                }
              }}
            />
          )}
        </Box>
      </Box>
      <Snackbar
        open={showSuccessUpdateMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
        onClose={() => setShowSuccessUpdateMessage(false)}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          {t('Your buyers auto-matching email settings have been updated!')}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={confirmDialog}
        title={t('Auto-matching is not enabled')}
        okText={t('enable')}
        cancelText={t('later')}
        onClose={() => setConfirmDialog(false)}
        onSubmit={() => {
          updateListing({
            variables: {
              id: listing.id,
              lot: {
                auto_matching_response_enabled: true,
              },
            },
            onCompleted: () => setConfirmDialog(false),
          });
        }}
      />
    </>
  );
};
